import React from "react";
import { alpha, useTheme } from "@mui/material/styles";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";

const fmt = (t) => new Date(t * 1000).toISOString().substr(14, 5);

export default function AudioCompare({ section, dense = false }) {
  const theme = useTheme();

  const [active, setActive] = React.useState(0);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [duration, setDuration] = React.useState(0);
  const [time, setTime] = React.useState(0);
  const [loop, setLoop] = React.useState(true);
  const [isScrubbing, setIsScrubbing] = React.useState(false);

  const tracks = React.useMemo(() => {
    if (Array.isArray(section?.audios) && section.audios.length) return section.audios;
    if (section?.audioSrc) return [{ src: section.audioSrc, label: section.audioLabel }];
    return [];
  }, [section]);

  const refs = React.useRef([]);
  const getRef = (i) => (el) => (refs.current[i] = el);

  // collect duration from all tracks (take max)
  const handleLoadedMeta = (e) => {
    const d = e.currentTarget.duration;
    if (!Number.isNaN(d) && d !== Infinity) {
      setDuration((prev) => Math.max(prev, d));
    }
  };

  // Keep only the active one audible
  const applyActiveMute = React.useCallback(() => {
    refs.current.forEach((a, i) => {
      if (!a) return;
      a.muted = i !== active;       // followers muted
      a.volume = i === active ? 1 : 0;
    });
  }, [active]);

  React.useEffect(() => {
    applyActiveMute();
  }, [applyActiveMute]);

  // Start all audios at the same time from `t`
  const startAllAt = async (t = 0) => {
    // prepare
    refs.current.forEach((a) => {
      if (!a) return;
      a.loop = loop;
      a.muted = true;       // temporarily mute all to satisfy Safari autoplay
      a.playsInline = true;
      try { a.currentTime = t; } catch { }
    });
    applyActiveMute();

    // play all
    try {
      await Promise.all(refs.current.map(async (a) => {
        if (!a) return;
        if (a.paused) await a.play();
      }));
      setIsPlaying(true);
    } catch {
      // If Safari blocked, the user needs to press Play after a gesture.
    }
  };

  const pauseAll = () => {
    refs.current.forEach((a) => a && a.pause());
    setIsPlaying(false);
  };

  const restartAll = () => {
    const wasPlaying = isPlaying;
    pauseAll();
    refs.current.forEach((a) => { if (a) a.currentTime = 0; });
    setTime(0);
    if (wasPlaying) startAllAt(0);
  };

  // Commit a seek: pause -> align -> resume (if needed)
  const commitSeek = (t) => {
    const wasPlaying = isPlaying;
    pauseAll();
    refs.current.forEach((a) => { if (a) a.currentTime = t; });
    setTime(t);
    if (wasPlaying) startAllAt(t);
  };

  // Lightweight drift check: if any track drifts a lot, re-align occasionally
  React.useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => {
      const master = refs.current[active];
      if (!master) return;
      const mt = master.currentTime || 0;
      let needsRealign = false;
      for (const a of refs.current) {
        if (!a || a === master) continue;
        if (Math.abs((a.currentTime || 0) - mt) > 0.35) { // ~350ms
          needsRealign = true; break;
        }
      }
      if (needsRealign) {
        const wasPlaying = true;
        pauseAll();
        refs.current.forEach((a) => { if (a) a.currentTime = mt; });
        if (wasPlaying) startAllAt(mt);
      }
      // also keep UI time from master
      setTime(mt);
    }, 700); // infrequent to avoid stutter
    return () => clearInterval(id);
  }, [isPlaying, active]);

  const togglePlay = () => (isPlaying ? pauseAll() : startAllAt(time));

  const toggleLoop = () => {
    const v = !loop;
    setLoop(v);
    refs.current.forEach((a) => { if (a) a.loop = v; });
  };

  const onTrackClick = (idx) => {
    setActive(idx);
    applyActiveMute();
  };

  if (tracks.length === 0) return null;

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      {/* Controls */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button size="small" variant="outlined" onClick={togglePlay}>
          {isPlaying ? "Pause" : "Play"}
        </Button>
        <Button size="small" variant="text" onClick={restartAll}>Restart</Button>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56, textAlign: "right" }}>
          {fmt(time)} / {duration ? fmt(duration) : "--:--"}
        </Typography>
      </Stack>

      {/* Seek bar (don’t live-seek; commit on release) */}
      <Box sx={{ px: 0.5 }}>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.01}
          value={isScrubbing ? undefined : time}
          onChange={(e) => {
            const t = parseFloat(e.target.value);
            setIsScrubbing(true);
            setTime(t); // update display only
          }}
          onMouseUp={(e) => { setIsScrubbing(false); commitSeek(parseFloat(e.currentTarget.value)); }}
          onTouchEnd={(e) => {
            const input = e.currentTarget;
            setIsScrubbing(false);
            commitSeek(parseFloat(input.value));
          }}
          onKeyUp={(e) => {
            if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
              const t = parseFloat(e.currentTarget.value);
              setIsScrubbing(false);
              commitSeek(t);
            }
          }}
          style={{ width: "100%" }}
          aria-label="Seek"
        />
      </Box>

      {/* Loop */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button size="small" variant={loop ? "contained" : "outlined"} onClick={toggleLoop}>
          {loop ? "Loop: On" : "Loop: Off"}
        </Button>
      </Stack>

      {/* Track list */}
      <Stack spacing={1}>
        {tracks.map((a, idx) => (
          <Box key={a.src || idx}
            onClick={() => onTrackClick(idx)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTrackClick(idx); } }}
            role="button"
            tabIndex={0}
            sx={{
              cursor: 'pointer', outline: 'none',
              transition: 'border-color 120ms ease, background-color 120ms ease',
              '&:hover': { borderColor: theme.palette.text.secondary, backgroundColor: alpha(theme.palette.background.paper, 0.75) },
              '&:focus-visible': { borderColor: theme.palette.primary.main, boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}` },
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              p: dense ? 1 : 1.25,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
              backdropFilter: "saturate(140%) blur(4px)",
            }}>
            <Stack spacing={0.25}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  {a.label || `Clip ${idx + 1}`}
                </Typography>
                <IconButton
                  size="small"
                  color={active === idx ? 'primary' : 'default'}
                  onClick={(e) => { e.stopPropagation(); onTrackClick(idx); }}
                >
                  {active === idx ? <VolumeUpRoundedIcon /> : <VolumeOffRoundedIcon />}
                </IconButton>
              </Stack>
              {a.caption && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {a.caption}
                </Typography>
              )}
              {/* Hidden audio elements */}
              <audio
                ref={getRef(idx)}
                preload="auto"
                src={a.src}
                loop={loop}
                playsInline
                onLoadedMetadata={handleLoadedMeta}
                // DO NOT do any per-frame sync here; we keep it simple.
                style={{ display: "none" }}
                aria-hidden
              />
            </Stack>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}
